import {
  Badge,
  Box,
  ButtonBase,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import { appThemes, useMeetingAppContext } from "../MeetingAppContextDef";
import useResponsiveSize from "../utils/useResponsiveSize";
import Lottie from "react-lottie";
import { getAnimationDuration } from "../utils/animationUtils";

/**
 * Shared icon button base component
 * Reduces duplication between MobileIconButton and OutlineIconButton
 */
const IconButtonBase = ({
  badge,
  onClick,
  Icon,
  isFocused,
  bgColor,
  focusIconColor,
  disabledOpacity,
  disabled,
  btnID,
  buttonText,
  lottieOption,
  mouseOver,
  mouseDown,
  onMouseEnter,
  onMouseLeave,
  onMouseDown,
  onMouseUp,
  color,
  padding = '1vh',
}) => {
  const theme = useTheme();
  const { animationsEnabled, appTheme } = useMeetingAppContext();

  const iconSize = useResponsiveSize({
    xl: '2.8vh',
    lg: '2.6vh',
    md: '2.4vh',
    sm: '2.2vh',
    xs: '2vh',
  });

  const getIconColor = () => {
    if (isFocused) {
      return focusIconColor || (appTheme === appThemes.LIGHT
        ? theme.palette.common.white
        : "#1C1F2E");
    }
    if (color) return color;
    return appTheme === appThemes.LIGHT ? "#95959E" : "#fff";
  };

  const iconColor = getIconColor();

  return (
    <ButtonBase
      id={btnID}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      disabled={disabled}
      onClick={onClick}
    >
      <Box
        style={{
          padding,
          opacity: disabled ? disabledOpacity || 0.7 : 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: '0.8vh',
          transform: `scale(${mouseOver ? (mouseDown ? 0.95 : 1.1) : 1})`,
          transition: `all ${getAnimationDuration(animationsEnabled, 200)}ms`,
          transitionTimingFunction: "linear",
        }}
      >
        {Icon && (
          <Badge max={1000} color={"secondary"} badgeContent={badge}>
            {lottieOption ? (
              <Box
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: bgColor,
                  padding: "0.5vh",
                  borderRadius: "0.5vh",
                }}
              >
                <Lottie
                  style={{ height: iconSize }}
                  options={lottieOption}
                  eventListeners={[{ eventName: "done" }]}
                  height={iconSize}
                  width={
                    (iconSize * lottieOption?.width) / lottieOption?.height
                  }
                  isClickToPauseDisabled
                />
              </Box>
            ) : (
              <Icon
                style={{
                  color: iconColor,
                  height: iconSize,
                  width: iconSize,
                }}
                fillColor={iconColor}
              />
            )}
          </Badge>
        )}
      </Box>
      {buttonText && (
        <Typography
          variant="subtitle2"
          style={{
            fontWeight: "bold",
            marginRight: '0.8vh',
            color: iconColor,
          }}
        >
          {buttonText}
        </Typography>
      )}
    </ButtonBase>
  );
};

export default IconButtonBase;
